import { useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from '@cortex/web-design-system/components/ui/Card';
import type {
  IdtDesignEvaluationPoint,
  IdtDesignEvaluationQuarterPoint,
} from '../../../lib/api.js';
import { monthLabelOf } from './periods.js';
import type { TrendView } from './shared.js';
import { DesignEvalSelect } from './DesignEvalSelect.js';
import { DesignEvaluationRadar } from './DesignEvaluationRadar.js';
import { DesignEvaluationTrend } from './DesignEvaluationTrend.js';
import { EvaluationDetailModal } from './EvaluationDetailModal.js';
import { TrendViewSwitch } from './TrendViewSwitch.js';
import { fyLabelOf, fyMonths, fyStartYearOf, type EvalSeriesPoint } from './designEvaluation.js';

export interface DesignEvaluationCardProps {
  /** 全体の月次評価系列 (月昇順) */
  points: IdtDesignEvaluationPoint[];
  /** 全体の四半期評価系列 (Q 昇順) */
  quarterPoints: IdtDesignEvaluationQuarterPoint[];
  /** 読み込み中フラグ */
  isLoading?: boolean;
}

type EvalMode = 'radar' | 'line';

/** 折線図の高さ。radar 状態の本文高さに合わせ、切替時にカード高が変わらないようにする。 @graph-connects none */
const TREND_HEIGHT = 480;

/** radar / 推移 を切替える pill segmented control。 @graph-connects none */
function ModeSwitch({ mode, onChange }: { mode: EvalMode; onChange: (next: EvalMode) => void }) {
  const { t } = useTranslation();
  const segClass = (active: boolean) =>
    `px-2.5 py-0.5 rounded-full text-[11px] font-semibold transition-colors leading-none ${
      active
        ? 'bg-[var(--color-bg-surface)] text-[var(--color-text-primary)] shadow-sm'
        : 'text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)]'
    }`;
  return (
    <div
      className="inline-flex items-center gap-0.5 rounded-full p-0.5 border border-[var(--color-border)]"
      style={{ background: 'var(--color-bg-elevated)' }}
      role="group"
      aria-label={t('idtKpi.designEvaluation.modeSwitchLabel')}
    >
      <button
        type="button"
        onClick={() => {
          onChange('radar');
        }}
        className={segClass(mode === 'radar')}
      >
        {t('idtKpi.designEvaluation.modeRadar')}
      </button>
      <button
        type="button"
        onClick={() => {
          onChange('line');
        }}
        className={segClass(mode === 'line')}
      >
        {t('idtKpi.designEvaluation.modeLine')}
      </button>
    </div>
  );
}

/**
 * IDT 看板「デザイン評価」カード。全体の PJ解像度 + チーム貢献度評価を、当期間 radar (前期間比較) と
 * 推移折線で切替表示する。月/Q スイッチで粒度、ドロップダウンで対象期間 (月 / Q / FY) を選ぶ。
 *
 * @graph-node {Page}
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business IDT 看板のデザイン評価カード。全体の解像度/貢献度を月/Q × radar(前期間比較)/推移で可視化 + 定性評価FB 明細モーダル導線
 * @graph-connects EvaluationDetailModal [calls] 対象月度の全メンバー評価明細モーダルの表示
 */
export function DesignEvaluationCard({
  points,
  quarterPoints,
  isLoading = false,
}: DesignEvaluationCardProps) {
  const { t } = useTranslation();
  const [mode, setMode] = useState<EvalMode>('radar');
  const [granularity, setGranularity] = useState<TrendView>('month');
  const [selectedMonth, setSelectedMonth] = useState<string | null>(null);
  const [selectedQuarter, setSelectedQuarter] = useState<string | null>(null);
  const [selectedFy, setSelectedFy] = useState<number | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);

  // 月次 / 四半期を period 統一の汎用系列に変換する。
  const monthSeries = useMemo<EvalSeriesPoint[]>(
    () =>
      points.map((p) => ({
        period: p.month,
        resolution: p.resolution,
        evaluation: p.evaluation,
        evaluationCounts: p.evaluationCounts,
      })),
    [points],
  );
  const quarterSeries = useMemo<EvalSeriesPoint[]>(
    () =>
      quarterPoints.map((p) => ({
        period: p.quarter,
        resolution: p.resolution,
        evaluation: p.evaluation,
        evaluationCounts: p.evaluationCounts,
      })),
    [quarterPoints],
  );

  const isQuarter = granularity === 'quarter';
  const activeSeries = isQuarter ? quarterSeries : monthSeries;
  const latestPeriod =
    activeSeries.length > 0 ? (activeSeries[activeSeries.length - 1]?.period ?? null) : null;
  const latestMonth =
    monthSeries.length > 0 ? (monthSeries[monthSeries.length - 1]?.period ?? null) : null;

  // period → 表示ラベル (月は "2026年6月" / Q はシート表記 "2026/4Q")。
  const fullLabelOf = useMemo(
    () => (period: string) => (isQuarter ? period : monthLabelOf(period)),
    [isQuarter],
  );
  const shortLabelOf = useMemo(
    () => (period: string) =>
      isQuarter
        ? period.slice(2)
        : `${period.slice(2, 4)}/${String(parseInt(period.slice(5, 7), 10))}`,
    [isQuarter],
  );

  // radar の選択期間 (粒度ごとに記憶) と前期間。
  const selectedPeriod = (isQuarter ? selectedQuarter : selectedMonth) ?? latestPeriod;
  const { selected, prev } = useMemo(() => {
    const idx = activeSeries.findIndex((p) => p.period === selectedPeriod);
    return {
      selected: idx >= 0 ? (activeSeries[idx] ?? null) : null,
      prev: idx > 0 ? (activeSeries[idx - 1] ?? null) : null,
    };
  }, [activeSeries, selectedPeriod]);

  // 推移折線の対象 period: 月は FY の 12 ヶ月、Q は全 Q。
  const effectiveFy = selectedFy ?? (latestMonth != null ? fyStartYearOf(latestMonth) : null);
  const linePeriods = useMemo(
    () =>
      isQuarter
        ? quarterSeries.map((p) => p.period)
        : effectiveFy != null
          ? fyMonths(effectiveFy)
          : [],
    [isQuarter, quarterSeries, effectiveFy],
  );

  // ドロップダウン候補。
  const radarOptions = useMemo(
    () =>
      [...activeSeries]
        .reverse()
        .map((p) => ({ value: p.period, label: isQuarter ? p.period : monthLabelOf(p.period) })),
    [activeSeries, isQuarter],
  );
  const fyOptions = useMemo(() => {
    const set = new Set<number>();
    for (const p of monthSeries) set.add(fyStartYearOf(p.period));
    return [...set].sort((a, b) => b - a).map((y) => ({ value: String(y), label: fyLabelOf(y) }));
  }, [monthSeries]);

  // ヘッダ右のドロップダウン (radar: 月/Q を選択, line+月: FY を選択, line+Q: なし)。
  let periodSelect: ReactNode = null;
  if (mode === 'radar') {
    if (selectedPeriod != null) {
      periodSelect = (
        <DesignEvalSelect
          options={radarOptions}
          value={selectedPeriod}
          onChange={isQuarter ? setSelectedQuarter : setSelectedMonth}
          ariaLabel={t('idtKpi.designEvaluation.periodSelectLabel')}
        />
      );
    }
  } else if (!isQuarter && effectiveFy != null) {
    periodSelect = (
      <DesignEvalSelect
        options={fyOptions}
        value={String(effectiveFy)}
        onChange={(v) => {
          setSelectedFy(parseInt(v, 10));
        }}
        ariaLabel={t('idtKpi.designEvaluation.fySelectLabel')}
      />
    );
  }

  const selectedLabel = selected ? fullLabelOf(selected.period) : '';
  const prevLabel = prev ? fullLabelOf(prev.period) : null;

  return (
    <Card className="animate-content-reveal">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between mb-5">
        <div>
          <h3 className="text-sm font-semibold text-[var(--color-text-primary)]">
            {t('idtKpi.designEvaluation.title')}
          </h3>
          <p className="text-xs text-[var(--color-text-muted)] mt-1">
            {t(
              mode === 'radar'
                ? 'idtKpi.designEvaluation.subtitleRadar'
                : isQuarter
                  ? 'idtKpi.designEvaluation.subtitleLineQuarter'
                  : 'idtKpi.designEvaluation.subtitleLine',
            )}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {periodSelect}
          <TrendViewSwitch view={granularity} onChange={setGranularity} />
        </div>
      </div>

      {isLoading ? (
        <div className="py-12 text-center text-sm text-[var(--color-text-muted)]">Loading…</div>
      ) : activeSeries.length === 0 ? (
        <div className="py-12 text-center text-sm text-[var(--color-text-muted)]">
          {t('idtKpi.designEvaluation.empty')}
        </div>
      ) : mode === 'radar' ? (
        <DesignEvaluationRadar
          selected={selected}
          prev={prev}
          selectedLabel={selectedLabel}
          prevLabel={prevLabel}
        />
      ) : (
        <DesignEvaluationTrend
          points={activeSeries}
          periods={linePeriods}
          shortLabelOf={shortLabelOf}
          fullLabelOf={fullLabelOf}
          height={TREND_HEIGHT}
        />
      )}

      {/* 左下=定性評価FB 明細への導線 / 右下=radar・推移の切替 */}
      <div className="flex items-center justify-between mt-4">
        <button
          type="button"
          onClick={() => {
            setDetailOpen(true);
          }}
          className="inline-flex items-center gap-1 rounded-md border border-[var(--color-border)] bg-[var(--color-bg-surface)] px-2.5 py-1 text-xs font-semibold text-[var(--color-text-secondary)] transition-colors hover:border-[var(--color-border-hover)] hover:text-[var(--color-text-primary)]"
        >
          {t('idtKpi.designEvaluation.detail.button')}
        </button>
        <ModeSwitch mode={mode} onChange={setMode} />
      </div>

      {/* 明細は月度が軸。Q / FY 表示中で月が定まらないときは null を渡し、シート内の最新月に委ねる */}
      <EvaluationDetailModal
        open={detailOpen}
        onClose={() => {
          setDetailOpen(false);
        }}
        initialMonth={isQuarter ? null : selectedPeriod}
      />
    </Card>
  );
}
